import ButtonDropdown from "@components/ButtonDropdown"
import {
  AltRoute,
  Cached,
  Edit,
  FileDownload,
  FileUpload,
  FindReplace,
  Repartition,
  Save,
} from "@mui/icons-material"
import { Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Typography } from "@mui/material"
import Groups2Icon from "@mui/icons-material/Groups2"
import { KeyBinding } from "@features/keymap/KeyBinding"
import { useKeymap } from "@features/keymap/useKeymap"
import { formatModifierKey } from "@features/keymap/keyShortcutFormatter"
import { shortcuts } from "@features/keymap/shortcuts"

export type ActionsDropdownButtonProps = {
  isPrivate: boolean
  onDeleteClick?: () => void
  onExposeClick?: () => void
  onSampleClick?: () => void
  onPublishInternallyClick?: () => void
  onSaveSnapshotClick?: () => void
  onRestoreSnapshotClick?: () => void
  onBulkNodesEditClick?: () => void
  onExportGraphClick?: () => void
  onImportGraphClick?: () => void
}

const ActionsDropdownButton = (props: ActionsDropdownButtonProps) => {
  const { getKeymaps } = useKeymap()
  const keymap = getKeymaps().byId

  return (
    <ButtonDropdown label="Actions" icon={<Edit />}>
      <MenuList>
        {props.isPrivate && (
          <MenuItem onClick={props.onDeleteClick} title={keymap.get("delete-graph")?.desc}>
            <KeyBinding bind={shortcuts["delete-graph"]} onKeyDown={props.onDeleteClick} />
            <ListItemIcon>
              <Groups2Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete private graph</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {formatModifierKey("shift")}+D
            </Typography>
          </MenuItem>
        )}
        {props.isPrivate && (
          <MenuItem onClick={props.onExposeClick} title={keymap.get("share-graph")?.desc}>
            <KeyBinding bind={shortcuts["share-graph"]} onKeyDown={props.onExposeClick} />
            <ListItemIcon>
              <Groups2Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Share with other builders</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {formatModifierKey("shift")}+E
            </Typography>
          </MenuItem>
        )}

        <MenuItem onClick={props.onSampleClick} title={keymap.get("sample-graph")?.desc}>
          <KeyBinding bind={shortcuts["sample-graph"]} onKeyDown={props.onSampleClick} />
          <ListItemIcon>
            <Cached fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sample</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("shift")}+S
          </Typography>
        </MenuItem>

        {!props.isPrivate && (
          <MenuItem
            onClick={props.onPublishInternallyClick}
            title={keymap.get("publish-graph")?.desc}
          >
            <KeyBinding
              bind={shortcuts["publish-graph"]}
              onKeyDown={props.onPublishInternallyClick}
            />
            <ListItemIcon>
              <AltRoute fontSize="small" />
            </ListItemIcon>
            <ListItemText>Publish internally</ListItemText>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              {formatModifierKey("shift")}+P
            </Typography>
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={props.onSaveSnapshotClick} title={keymap.get("save-snapshot")?.desc}>
          <KeyBinding bind={shortcuts["save-snapshot"]} onKeyDown={props.onSaveSnapshotClick} />
          <ListItemIcon>
            <Save fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save snapshot</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("shift")}+Q
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={props.onRestoreSnapshotClick}
          title={keymap.get("restore-snapshot")?.desc}
        >
          <KeyBinding
            bind={shortcuts["restore-snapshot"]}
            onKeyDown={props.onRestoreSnapshotClick}
          />
          <ListItemIcon>
            <Repartition fontSize="small" />
          </ListItemIcon>
          <ListItemText>Restore snapshot</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("shift")}+W
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={props.onBulkNodesEditClick} title={keymap.get("bulk-nodes-edit")?.desc}>
          <KeyBinding bind={shortcuts["bulk-nodes-edit"]} onKeyDown={props.onBulkNodesEditClick} />
          <ListItemIcon>
            <FindReplace fontSize="small" />
          </ListItemIcon>
          <ListItemText>Bulk edit nodes</ListItemText>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatModifierKey("shift")}+R
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem onClick={props.onExportGraphClick} title="Export graph">
          <ListItemIcon>
            <FileDownload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export graph</ListItemText>
        </MenuItem>

        <MenuItem onClick={props.onImportGraphClick} title="Import graph">
          <ListItemIcon>
            <FileUpload fontSize="small" />
          </ListItemIcon>
          <ListItemText>Import graph</ListItemText>
        </MenuItem>
      </MenuList>
    </ButtonDropdown>
  )
}

export default ActionsDropdownButton
